/**
 * Analytics tracking utility for 1895
 * Tracks user actions, errors, and session data
 * Does NOT expose actual content to admin
 */

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Generate unique session ID
const generateSessionId = () => {
  return 'sess_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
};

// Get or create analytics session ID
let analyticsSessionId = sessionStorage.getItem('analytics_session_id');
let actionsCount = parseInt(sessionStorage.getItem('actions_count') || '0');

if (!analyticsSessionId) {
  analyticsSessionId = generateSessionId();
  sessionStorage.setItem('analytics_session_id', analyticsSessionId);
  
  // Start session tracking
  startSession();
}

// Track session end on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    endSession();
  });
  
  // Also track on visibility change (mobile)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      endSession();
    }
  });
}

async function startSession() {
  try {
    await fetch(`${API}/analytics/session/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_id: analyticsSessionId })
    });
  } catch (e) {
    console.debug('Analytics session start failed:', e);
  }
}

async function endSession() {
  try {
    // Use sendBeacon for reliability on page unload
    const data = JSON.stringify({
      session_id: analyticsSessionId,
      actions_count: actionsCount
    });
    
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/analytics/session/end`, data);
    } else {
      await fetch(`${API}/analytics/session/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: data,
        keepalive: true
      });
    }
  } catch (e) {
    console.debug('Analytics session end failed:', e);
  }
}

/**
 * Track a user event
 * @param {string} eventType - Type of event (click, generate, create, edit, etc.)
 * @param {string} category - Category (project, shot, character, location, etc.)
 * @param {object} data - Additional data (sanitized server-side)
 */
export async function trackEvent(eventType, category = 'general', data = {}) {
  try {
    actionsCount++;
    sessionStorage.setItem('actions_count', actionsCount.toString());
    
    await fetch(`${API}/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        event_type: eventType,
        category,
        data,
        page: window.location.pathname,
        session_id: analyticsSessionId
      })
    });
  } catch (e) {
    console.debug('Analytics track failed:', e);
  }
}

/**
 * Track an error
 * @param {string} errorType - Type of error (api_error, render_error, etc.)
 * @param {Error|string} error - The error object or message
 * @param {string} action - What the user was trying to do
 */
export async function trackError(errorType, error, action = '') {
  try {
    const errorMessage = error?.message || String(error);
    const errorStack = error?.stack || '';
    
    await fetch(`${API}/analytics/error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        error_type: errorType,
        message: errorMessage,
        stack: errorStack,
        page: window.location.pathname,
        action
      })
    });
  } catch (e) {
    console.debug('Analytics error track failed:', e);
  }
}

// Predefined event types for consistency
export const EventTypes = {
  // Auth
  LOGIN: 'login',
  LOGOUT: 'logout',
  SIGNUP: 'signup',
  
  // Projects
  PROJECT_CREATE: 'project_create',
  PROJECT_OPEN: 'project_open',
  PROJECT_DELETE: 'project_delete',
  PROJECT_SHARE: 'project_share',
  
  // Generation
  SHOT_GENERATE: 'shot_generate',
  CHARACTER_GENERATE: 'character_generate',
  LOCATION_GENERATE: 'location_generate',
  SCRIPT_ANALYZE: 'script_analyze',
  
  // Editing
  SHOT_EDIT: 'shot_edit',
  CHARACTER_EDIT: 'character_edit',
  SCRIPT_EDIT: 'script_edit',
  
  // UI Actions
  BUTTON_CLICK: 'button_click',
  MODAL_OPEN: 'modal_open',
  TAB_SWITCH: 'tab_switch',
  
  // Collaboration
  TEAM_INVITE: 'team_invite',
  INVITE_ACCEPT: 'invite_accept',
};

export const Categories = {
  PROJECT: 'project',
  SHOT: 'shot',
  CHARACTER: 'character',
  LOCATION: 'location',
  SCRIPT: 'script',
  COLLABORATION: 'collaboration',
  AUTH: 'auth',
  UI: 'ui'
};

// Error types
export const ErrorTypes = {
  API_ERROR: 'api_error',
  RENDER_ERROR: 'render_error',
  VALIDATION_ERROR: 'validation_error',
  AUTH_ERROR: 'auth_error',
  NETWORK_ERROR: 'network_error',
  GENERATION_ERROR: 'generation_error'
};

export default {
  trackEvent,
  trackError,
  EventTypes,
  Categories,
  ErrorTypes
};
